import styled from 'styled-components';
import { HuddleCard, OrgId } from '../../../../../common/src/model.ts';
import { gmailComposeUrl, huddleEmailSubject } from './gmail_compose_url.ts';
import { useHuddleOrgNotes } from './use_huddle.ts';

// The two cards do different jobs in the standup, so they are deliberately easy to tell apart at a
// glance: the active card is a thanksgiving, the inactive card is the one that needs an action.
const cardStyles = {
  active: { accent: 'var(--color-nightingale)', eyebrow: 'Celebrate' },
  inactive: { accent: 'var(--color-gold-600)', eyebrow: 'Reach out' },
};

// users.name is the literal 'Unknown' for someone who signed up without ever giving a name, which
// is no name at all on a card whose point is to know who to write to - their email says more
const unknownName = 'Unknown';

function adminDisplayName(admin: HuddleCard['admins'][number]): string {
  return admin.name && admin.name !== unknownName ? admin.name : (admin.email ?? admin.name);
}

export const PrayerFocusCard = ({ card }: { card: HuddleCard }) => {
  const { accent, eyebrow } = cardStyles[card.orgClass];
  return (
    <Card $accent={accent}>
      <Eyebrow $accent={accent}>{eyebrow}</Eyebrow>
      <OrgName>{card.name}</OrgName>
      <OrgSubtitle>
        {card.location} &middot; {card.type}
      </OrgSubtitle>

      <Stats>
        <Stat>
          <StatValue>{card.householdCount}</StatValue>
          <StatLabel>{card.householdCount === 1 ? 'household' : 'households'}</StatLabel>
        </Stat>
        <Stat>
          <StatValue>{card.songViews30Days}</StatValue>
          <StatLabel>songs sung (30 days)</StatLabel>
        </Stat>
        <Stat>
          <StatValue>{card.daysSinceLastSongView ?? '—'}</StatValue>
          <StatLabel>{card.daysSinceLastSongView === undefined ? 'never sung' : 'days since last song'}</StatLabel>
        </Stat>
      </Stats>

      {card.outreachAngle && (
        <OutreachAngle $accent={accent}>
          <SectionHeading>Outreach angle</SectionHeading>
          <div>{card.outreachAngle}</div>
        </OutreachAngle>
      )}

      <OrgNotes orgId={card.orgId} />

      <Section>
        <SectionHeading>Administrators</SectionHeading>
        {card.admins.length === 0 ? (
          <Empty>No administrators</Empty>
        ) : (
          <List>
            {card.admins.map((admin) => (
              <ListItem key={admin.email ?? admin.name}>
                {/* email is optional - an admin invited by link may not have one yet, and there is
                    nothing to write to, so that one stays plain text */}
                {admin.email ? (
                  <AdminLink
                    href={gmailComposeUrl({ to: admin.email, subject: huddleEmailSubject(card.name) })}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <span>{adminDisplayName(admin)}</span>
                    {/* no second copy of the address when it is already standing in for the name */}
                    {adminDisplayName(admin) !== admin.email && <Detail>{admin.email}</Detail>}
                  </AdminLink>
                ) : (
                  <span>{adminDisplayName(admin)}</span>
                )}
              </ListItem>
            ))}
          </List>
        )}
      </Section>

      <Section>
        <SectionHeading>Top singers (30 days)</SectionHeading>
        {card.topUsers.length === 0 ? (
          <Empty>No one has sung in the last 30 days</Empty>
        ) : (
          <List>
            {card.topUsers.map((user) => (
              <ListItem key={user.name}>
                <span>{user.name}</span>
                <Detail>
                  {user.songViewCount} {user.songViewCount === 1 ? 'song' : 'songs'}
                </Detail>
              </ListItem>
            ))}
          </List>
        )}
      </Section>
    </Card>
  );
};

// The consult/demo one-pager summary from the Google shared drive.  With no Google credential
// configured - the normal state today - the server returns {}, and this renders nothing at all: no
// heading, no placeholder, nothing that reads as broken.
const OrgNotes = ({ orgId }: { orgId: OrgId }) => {
  const { notes, isLoading, error } = useHuddleOrgNotes(orgId);
  if (isLoading || (!error && !notes?.summary && !notes?.notice)) {
    return null;
  }
  return (
    <Section>
      <SectionHeading>Consult notes</SectionHeading>
      {error && <Empty>Notes unavailable: {error}</Empty>}
      {!error && notes?.notice && <Empty>{notes.notice}</Empty>}
      {!error && notes?.summary && <div>{notes.summary}</div>}
      {!error && notes?.summary && notes.documentUrl && (
        <NotesLink href={notes.documentUrl} target="_blank" rel="noreferrer">
          {notes.documentName ?? 'Open document'}
        </NotesLink>
      )}
    </Section>
  );
};

const Card = styled.div<{ $accent: string }>`
  flex: 1 1 320px;
  max-width: 480px;
  text-align: left;
  padding: 16px 20px 20px;
  border: 1px solid var(--color-hairline);
  border-top: 6px solid ${({ $accent }) => $accent};
  border-radius: 10px;
  background-color: var(--color-background);
`;

const Eyebrow = styled.div<{ $accent: string }>`
  font-family: Jost-SemiBold, Arial, sans-serif;
  font-size: 0.8rem;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: ${({ $accent }) => $accent};
`;

const OrgName = styled.h2`
  margin: 4px 0 0;
  text-align: left;
  font-size: 1.5rem;
`;

const OrgSubtitle = styled.div`
  color: var(--color-text-darker);
  margin-bottom: 16px;
`;

const Stats = styled.div`
  display: flex;
  gap: 24px;
  flex-wrap: wrap;
  padding: 12px 0;
  border-top: 1px solid var(--color-hairline);
  border-bottom: 1px solid var(--color-hairline);
`;

const Stat = styled.div`
  display: flex;
  flex-direction: column;
`;

const StatValue = styled.span`
  font-family: Jost-SemiBold, Arial, sans-serif;
  font-size: 1.6rem;
  line-height: 1.1;
`;

const StatLabel = styled.span`
  font-size: 0.85rem;
  color: var(--color-text-darker);
`;

const Section = styled.div`
  margin-top: 16px;
`;

const SectionHeading = styled.div`
  font-family: Jost-SemiBold, Arial, sans-serif;
  font-size: 0.95rem;
  margin-bottom: 4px;
`;

const OutreachAngle = styled(Section)<{ $accent: string }>`
  padding: 12px;
  border-radius: 8px;
  border-left: 4px solid ${({ $accent }) => $accent};
  background-color: var(--color-background-template);
`;

const List = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
`;

const ListItem = styled.li`
  display: flex;
  justify-content: space-between;
  gap: 12px;
  padding: 2px 0;
`;

const Detail = styled.span`
  color: var(--color-text-darker);
  font-size: 0.9rem;
`;

// keeps the name and email laid out exactly as the plain-text admins beside it, so the row does not
// jump about depending on whether there is an address to write to
const AdminLink = styled.a`
  display: flex;
  justify-content: space-between;
  gap: 12px;
  width: 100%;
  color: var(--color-link);
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

const Empty = styled.div`
  color: var(--color-text-lighter);
  font-style: italic;
`;

const NotesLink = styled.a`
  display: inline-block;
  margin-top: 4px;
  font-size: 0.9rem;
  color: var(--color-link);
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;
